import {
  Button,
  Card,
  Descriptions,
  List,
  Space,
  Typography,
  message,
} from "antd";
import _ from "lodash";
import { useMemo } from "react";
import { useMutation, useQueryClient } from "react-query";
import { putOrderRepairOnSiteByOrderNo } from "../../../../../apis/Order";
import useModals from "../../../../../hooks/useModals";
import useReservation from "../../../../../hooks/useReservation";
import { CustomerOrderProductInfo } from "../../../../../model/OrderManagement/CustomerOrder";
import { CustomerOrderReservationInfo } from "../../../../../model/OrderManagement/CustomerOrder/Reservation";
import { HttpError } from "../../../../../network/types";
import { dateFormat } from "../../../../../shared/utils/Dates/format";
import color from "../../../../../theme/color";
import { ItemDescriptions } from "../../../Description/Item";
import { getKeyEnum, getValueEnum } from "../../../Unbookable/utils";
import { AddressForm } from "../../Detail/Address";
import { CustomCard } from "../../Detail/Card.styled";
import { ReservationOnsiteDateTimeChangeModal } from "../../Modal/Onsite/DateTime";
import { OnsiteStatusTag } from "../../Tags/OnsiteStatus";
import { RepairStatusTag } from "../../Tags/RepairStatus";
import { GroupCategory } from "../../utils/GroupCategory";
import { RepairMethodStatus } from "../../utils/Method";
import { ReservationStatus } from "../../utils/ReservationStatus";
import { RepairOrderItemBoxForm } from "../RepairCard/Item";
import { Section } from "../index.styled";

interface RepairOrderInfoFormProps {
  repairNo?: string;
  reservationInfo?: Array<CustomerOrderReservationInfo>;
  productInfo?: Array<CustomerOrderProductInfo>;
  isDomestic?: boolean;
}

/**
 * 매장주문 > 고객 정보 - 차량정보
 * @TODO 외부채널 기본차량정보..
 * @param param0
 * @returns
 */
export const RepairOrderInfoForm: React.FC<RepairOrderInfoFormProps> = ({
  repairNo,
  reservationInfo,
  productInfo,
  isDomestic,
}) => {
  const queryClient = useQueryClient();

  const { openModal, closeModal } = useModals();
  const { reservation } = useReservation();

  /**
   * 출장 예약일시 변경 로직
   */
  const { mutate: changeOnsiteMutate } = useMutation(
    putOrderRepairOnSiteByOrderNo,
    {
      onSuccess: () => {
        message.success("예약일시가 변경되었습니다.");
        closeModal();
        queryClient.refetchQueries(["OM", repairNo]);
        queryClient.refetchQueries(["reservations"]);
      },
      onError: (err: HttpError) => {
        message.error(err.message);
      },
    }
  );

  /**
   *  타이어 + 휠 | 엔진오일 | 와이퍼
   */
  const groupOrderItem = useMemo(() => {
    const groupItem = _.groupBy(productInfo || [], (item) => {
      const mappingName =
        // (item.productCategoryName === "휠얼라인먼트" && "타이어") ||
        item.productCategoryName;
      return mappingName && getKeyEnum(mappingName, GroupCategory);
    });

    const keys = Object.keys(GroupCategory);
    return groupItem
      ? Object.entries(groupItem).sort((a, b) => {
          // GroupCategory key 순서대로 정렬
          return keys.indexOf(a[0]) - keys.indexOf(b[0]);
        })
      : undefined;
  }, [productInfo]);

  const repair = useMemo(() => {
    const grouping = reservationInfo?.reduce((a: any, b: any) => {
      const key = getKeyEnum(b.reservationType, ReservationStatus);
      if (key && !a[key]) {
        a[key] = { ...b } as CustomerOrderReservationInfo;
      }

      return a;
    }, {});

    return { ...grouping };
  }, [reservationInfo]);

  const repairType = useMemo(() => {
    return repair.valet ? "VALET" : repair.onSite ? "ON_SITE" : "VISIT";
  }, [repair]);

  return (
    <Section style={{ padding: 0 }}>
      <CustomCard
        bordered={false}
        title={<Typography.Title level={5}>주문정보</Typography.Title>}
        headStyle={{ padding: "8px 20px", borderBottomWidth: 0 }}
        bodyStyle={{ padding: "0 20px 20px" }}
      >
        <ItemDescriptions
          colon={false}
          column={1}
          layout="horizontal"
          labelStyle={{ color: color.gray5, width: 100, fontWeight: 500 }}
          className="info"
        >
          <Descriptions.Item label="예약상태">
            {repairType === "ON_SITE" ? (
              <OnsiteStatusTag
                value={repair?.onSite?.reservedStatus}
                size="large"
              />
            ) : (
              (repair.repair?.reservedStatus && (
                <RepairStatusTag
                  value={repair.repair?.reservedStatus}
                  size="large"
                />
              )) ||
              "-"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="주문일시">
            {(reservation?.orderedAt &&
              dateFormat(reservation.orderedAt, "YYYY.MM.DD HH:mm")) ||
              "-"}
          </Descriptions.Item>
          {/* 출장 | 방문 체크 */}
          {repairType === "ON_SITE" ? (
            <Descriptions.Item label="출장예약일시">
              <Space size={4} align="center">
                <Typography.Text>
                  {(repair.onSite?.onSiteReservedAt &&
                    dateFormat(
                      repair.onSite.onSiteReservedAt,
                      "YYYY.MM.DD(dd) HH:mm"
                    )) ||
                    "-"}
                </Typography.Text>
                <Button
                  ghost
                  size="small"
                  type="default"
                  shape="round"
                  style={{
                    fontSize: 13,
                    height: 18,
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                  disabled={
                    !["PENDING", "COMPLETED_PAYMENT", "APPROVED"].includes(
                      repair.onSite.reservedStatus
                    )
                  }
                  onClick={() => {
                    openModal(
                      <ReservationOnsiteDateTimeChangeModal
                        defaultValue={repair.onSite.onSiteReservedAt}
                        onClose={(value) => {
                          if (value && repairNo) {
                            changeOnsiteMutate({
                              orderNo: repairNo as string,
                              onSitedAt: value?.toISOString(),
                            });
                          }
                        }}
                      />
                    );
                  }}
                >
                  변경
                </Button>
              </Space>
            </Descriptions.Item>
          ) : (
            <Descriptions.Item label="예약일시">
              {/* 출장 | 방문 체크 */}
              {(repair.repair?.repairReservedAt &&
                dateFormat(
                  repair.repair.repairReservedAt,
                  "YYYY.MM.DD(dd) HH:mm"
                )) ||
                "-"}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="방문방법">
            {(repairType && getValueEnum(repairType, RepairMethodStatus)) ||
              "-"}
          </Descriptions.Item>
          {repairType === "ON_SITE" && (
            <Descriptions.Item label="출장위치">
              <AddressForm
                street={repair.onSite?.street}
                zibun={repair.onSite.zibun}
                detail={repair.onSite?.detail}
              />
            </Descriptions.Item>
          )}
          {repairType === "ON_SITE" && (
            <Descriptions.Item label="출장요청사항">
              {repair.onSite?.memo || "-"}
            </Descriptions.Item>
          )}
          {!!groupOrderItem?.length && (
            <Descriptions.Item>
              <List
                dataSource={groupOrderItem || undefined}
                split={false}
                style={{ width: "100%" }}
                renderItem={(item: any) => (
                  <List.Item
                    key={`${repairNo}:${item.key}`}
                    style={{ width: "100%" }}
                  >
                    <Card
                      style={{ width: "100%" }}
                      bodyStyle={{ padding: 0, width: "100%" }}
                      bordered={false}
                    >
                      <RepairOrderItemBoxForm
                        groupItem={item}
                        isDomestic={isDomestic}
                      />
                    </Card>
                  </List.Item>
                )}
              />
            </Descriptions.Item>
          )}
        </ItemDescriptions>
      </CustomCard>
    </Section>
  );
};
